import React, { useContext, useEffect, useState } from "react";
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import { useTranslation } from "react-i18next";
import { ParentScreenCtx } from "../../../pages/workbench_build/WorkbenchBuild";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, debounce, isArray } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../redux/slices/treeSlice";
import CommonGrid from "../../Repository/CommonGrid";
import { Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LightTooltip from "../../../commonComponents/LightTooltip";

const CustomHeaderComponent = ({ displayName, showTooltip }) => {
  return (
    <div className="flex space-between align-center">
      {showTooltip && (
        <LightTooltip arrow title={"Click to select a row"}>
          <HelpOutlineIcon
            className="material-symbols-outlined"
            style={{ cursor: "pointer" }}
          />
        </LightTooltip>
      )}
      <div className="margin-left-4">{displayName}</div>
    </div>
  );
};

const initialRows = [
  { id: 1, errorType: t('RUL_MAX_LOOP_ERR'), selected: false },
  { id: 2, errorType: t('RUL_REQ_REC_FLD_MISSING'), selected: false },
  { id: 3, errorType: t('RUL_MIN_MAX_FLD_LENGTH_ERR'), selected: false },
  { id: 4, errorType: t('RUL_FLD_RANGE_MIN_MAX_LENGTH_ERR'), selected: false },
  { id: 5, errorType: t('RUL_DATE_RANGE_MIN_MAX_ERR'), selected: false },
  { id: 6, errorType: t('RUL_REG_EXPR_ERR'), selected: false },
  { id: 7, errorType: t('RUL_FLD_TYPE_MISMATCH_ERR'), selected: false },
  { id: 8, errorType: t('RUL_SIT_FLD_RULE_ERR_COND_LIST'), selected: false },
  { id: 9, errorType: t('RUL_SIT_FLD_RULE_ERR_EXCLUSION'), selected: false },
  { id: 10, errorType: t('RUL_SIT_FLS_RULE_ERR_COND'), selected: false },
  { id: 11, errorType: t('RUL_SIT_FLD_RULE_ERR_PAIRING'), selected: false },
];
const mapChkBox = {
  MAX_LOOPS: t("RUL_MAX_LOOP_ERR"),
  REQUIRED: t("RUL_REQ_REC_FLD_MISSING"),
  LENGTH: t("RUL_MIN_MAX_FLD_LENGTH_ERR"),
  RANGE: t("RUL_FLD_RANGE_MIN_MAX_LENGTH_ERR"),
  DATE_RANGE: t("RUL_DATE_RANGE_MIN_MAX_ERR"),
  REGULAR_EXPRESSION: t("RUL_REG_EXPR_ERR"),
  DATE_INT: t("RUL_FLD_TYPE_MISMATCH_ERR"),
  LIST_CONDITIONAL: t("RUL_SIT_FLD_RULE_ERR_COND_LIST"),
  EXCLUSION: t("RUL_SIT_FLD_RULE_ERR_EXCLUSION"),
  CONDITIONAL: t("RUL_SIT_FLS_RULE_ERR_COND"),
  PAIRING: t("RUL_SIT_FLD_RULE_ERR_PAIRING"),
};

function FilteringOptions() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx)
  const { rightTree } = useSelector(state => state.treeData[parentScreen] || {});
  const currentTab = rightTree.currentTab;
  const { selectedNode } = rightTree?.tree[rightTree.currentTab];
  const templateHeader = rightTree?.tree?.templateHeader;
  const [allRecords, setAllRecords] = useState(true);
  const [recordsWithoutErrors, setRecordWithoutErrors] = useState(false);
  const [recordsWithErrors, setRecordWithErrors] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [rows, setRows] = useState(initialRows);
  const [errorType, setErrorType] = useState("");
  const [showSelectedErrorChkbox, setShowSelectedErrorChkbox] = useState(false);


  const updateContentInRedux = (key, content, flag = true) => {
    const encodedContent = content ? content.replace(/\./g, "%1E") : content;
    let translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);
    eachDeep(translatedData, (objValue) => {
      if (objValue?.key !== selectedNode?.key) {
        return;
      }
      if (!encodedContent || encodedContent === "") {
        objValue[key] = encodedContent;
      } else if (key in objValue && objValue[key].includes("NONE%1E") && flag) {
        objValue[key] = "NONE%1E" + encodedContent;
      } else {
        objValue[key] = encodedContent;
      }
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: currentTab,
          data: objValue,
        })
      );
      if (isArray(objValue?.children)) {
        return objValue.children;
      }
    });
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: currentTab,
        data: translatedData,
      })
    );
  };

  const debouncedContentUpdate = debounce(updateContentInRedux, 100);

  const getKeyByValue = (object, value) => {
    return Object.keys(object).find((key) => object[key] === value);
  };

  const getValues = (rows) => {
    return rows.reduce((acc, next) => {
      if (next.selected) {
        const key = getKeyByValue(mapChkBox, next.errorType);
        if (key !== undefined) {
          acc += (acc ? "%1E" : "") + key;
        }
      }
      return acc;
    }, "");
  };

  const handleRow = (event) => {
    const { data } = event.node;
    
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === data.id ? { ...row, selected: !row.selected } : row
      );

      const value = getValues(updatedRows);
      setErrorType(value);
      debouncedContentUpdate("ERROR_TYPE", value);

      return updatedRows;
    });
  };

  const gridOptions = {
    columnDefs: [
      {
        headerName: t("COL_ERR_TYPE"),
        field: "errorType",
        resizable: false,
        headerComponent: CustomHeaderComponent,
        headerComponentParams: {
          showTooltip: true,
          displayName: t("COL_ERR_TYPE"),
        },
      },
    ],
  };

  const handleAllRecordsCheck = (checked) => {
    if (!(recordsWithoutErrors || recordsWithErrors)) {
      setAllRecords(true);
    } else if (checked) {
      setAllRecords(true);
      setRecordWithoutErrors(false);
      setRecordWithErrors(false);
      debouncedContentUpdate("ERROR_TYPE", "");
    } else {
      setAllRecords(false);
    }
  };

  const handleRecordsWithoutErrorsCheck = (checked) => {
    if (checked) {
      setRecordWithoutErrors(true);
      setAllRecords(false);
      debouncedContentUpdate("ERROR_TYPE", `NONE.${errorType}`);
      setErrorType((prev) => "NONE%1E" + (prev ?? ""));
    } else {
      setRecordWithoutErrors(false);
      if (!recordsWithErrors) {
        setAllRecords(true);
        debouncedContentUpdate("ERROR_TYPE", "");
      } else {
        const val = errorType.replace("NONE%1E", "");
        setTimeout(() => {
          debouncedContentUpdate("ERROR_TYPE", val, false);
        }, 100);
      }
    }
  };

  const handleRecordsWithErrorsCheck = (checked) => {
    if (checked) {
      setRecordWithErrors(true);
      setAllRecords(false);
    } else {
      setRecordWithErrors(false);
      if (!recordsWithoutErrors) {
        setAllRecords(true);
        debouncedContentUpdate("ERROR_TYPE", "");
      } else {
        debouncedContentUpdate("ERROR_TYPE", "NONE%1E");
      }
    }
  };

  useEffect(() => {
    if (!gridApi) return;
    
    requestAnimationFrame(() => {
      gridApi.forEachNode((node) => {
        const rowData = node.data;
        const shouldBeSelected = rowData.selected === true;
        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected, false); 
        }
      });
    });
  }, [rows, gridApi]);
  
  useEffect(() => {
    const { LOOP_SOURCE_NAME, SOURCE_TYPE, ERROR_TYPE } = selectedNode;
    
    const shouldShowCheckbox = 
      templateHeader?.FILETYPE === "XML" ||
      (LOOP_SOURCE_NAME && LOOP_SOURCE_NAME !== t("SELECT")) ||
      SOURCE_TYPE === "INBOUND_DATA";
    
    setShowSelectedErrorChkbox(shouldShowCheckbox);
    if (ERROR_TYPE) {
      const decodedErrorType = decodeURIComponent(ERROR_TYPE).split("\x1E");
      const err = [...new Set(decodedErrorType.filter((item) => item !== ""))];
      if (err.length) {
        setAllRecords(false);
        setErrorType(err.join("%1E"));
        if (err.includes("NONE")) {
          setRecordWithoutErrors(err[0] === "NONE");
        }
        if (err.some((item) => item !== "NONE")) {
          setRecordWithErrors(true);

          const updatedRows = initialRows.map((row) => {
            const mappedErrorType = Object.keys(mapChkBox).find(
              (key) => mapChkBox[key] === row.errorType
            );
            if (mappedErrorType && err.includes(mappedErrorType)) {
              return { ...row, selected: true };
            }
            return row;
          });

          setRows(updatedRows);
        }
      }
    }
  }, [selectedNode?.key, selectedNode?.ERROR_TYPE, selectedNode?.LOOP_SOURCE_NAME, selectedNode?.SOURCE_TYPE, gridApi]);

  useEffect(() => {
    if (!gridApi) return;

    if (allRecords || (recordsWithErrors && (!errorType || errorType === ""))) {
      if (allRecords) {
        setErrorType("");
        setRecordWithoutErrors(false);
      }
      const resetRows = initialRows.map(row => ({ ...row, selected: false }));
      setRows(resetRows);
      requestAnimationFrame(() => {
        gridApi.deselectAll();
      });
    }
  }, [allRecords, recordsWithErrors, errorType, gridApi]);

  return (
    <>
      <div className="margin-12">
        <TPCheckbox
          label={t("BTN_INC_ALL_RECORDS_REGARDLESS_OF_ERRORS_DEFAULT")}
          checked={allRecords}
          handleCheck={handleAllRecordsCheck}
        />
        {showSelectedErrorChkbox ? (
          <>
            <TPCheckbox
              label={t("BTN_INC_RECORDS_THAT_DONT_HAVE_ERRORS")}
              checked={recordsWithoutErrors}
              handleCheck={handleRecordsWithoutErrorsCheck}
            />
            <TPCheckbox
              label={t("BTN_INC_RECORDS_THAT_HAVE_FOLLOWING_ERRORS")}
              checked={recordsWithErrors}
              handleCheck={handleRecordsWithErrorsCheck}
            />
          </>
        ) : (
          <Typography fontStyle={"italic"} variant="h6" className="margin-4">
            {" "}
            {t("LBL_NOTE_DATA_VALIDATION_NOT_ALLOWED")}{" "}
          </Typography>
        )}

        {recordsWithErrors && (
          <div className="margin-8 margin-top-16">
            <CommonGrid
              gridOptions={gridOptions}
              rowData={rows}
              getGridAPI={(gridOptions, gridApi) => setGridApi(gridApi.api)}
              containerClasses={"filtering-options-grid-container"}
              gridProps={{
                onRowClicked: handleRow,
                rowSelection: "multiple",
                rowHeight: 30,
                suppressRowClickSelection: true,
                suppressRowDeselection: false,
                rowMultiSelectWithClick: false,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default FilteringOptions;
